import * as B from '@blueprintjs/core';
import * as S from '@blueprintjs/select';
import React from 'react';

import {ApiFeature} from 'app/modules/Remote/Feature/types';
import {alphanumericSort} from 'app/utils/featureUtils';

import cs from './styles.module.scss';

interface FeatureMultiSelectProps {
  features: ApiFeature[];
  onSelectionUpdate: (selection: ApiFeature[]) => void;
  initialSelection?: ApiFeature[];
  placeholder?: string;
  radioSelectionProps?: RadioSelectionProps;
  resetButtonProps?: {
    hidden: boolean;
    tooltip: string;
  };
  popoverProps?: B.PopoverProps;
  className?: string;
  disabled?: boolean;
}

interface RadioSelectionProps {
  title?: string | null;
  allText?: string;
  noneText?: string;
  someText?: string;
}

const defaultRadioText: RadioSelectionProps = {
  title: 'Select properties:',
  allText: 'All properties',
  noneText: 'No properties',
  someText: 'Select specific properties',
};

export const FeatureMultiSelect: React.FC<FeatureMultiSelectProps> = ({
  features,
  onSelectionUpdate,
  initialSelection = [],
  placeholder,
  resetButtonProps,
  popoverProps,
  className,
  disabled = false,
}) => {
  const MultiSelect = S.MultiSelect.ofType<ApiFeature>();
  const [selectedItems, setSelectedItems] = React.useState<ApiFeature[]>(initialSelection);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const {allText, noneText, someText} = defaultRadioText;
  const {hidden: resetButtonHidden, tooltip: resetButtonTooltip} = resetButtonProps || {
    hidden: true,
    tooltip: 'Reset to initial selection',
  };

  const composeName = (feature: ApiFeature): string =>
    feature.properties.multiFeaturePartName
      ? feature.properties.name + ' > ' + feature.properties.multiFeaturePartName
      : feature.properties.name;

  const sortedFeatures = React.useMemo(
    () => features.sort((a, b) => alphanumericSort(composeName(a), composeName(b))),
    [features]
  );

  React.useEffect(() => {
    onSelectionUpdate(selectedItems);
  }, [selectedItems, onSelectionUpdate]);

  // Determine current segment: use explicit mode if set, otherwise derive from selection
  const currentSegment: 'all' | 'none' | 'some' =
    selectedItems.length === features.length && features.length > 0
      ? 'all'
      : selectedItems.length === 0
        ? 'none'
        : 'some';

  const handleSegmentChange = (value: string): void => {
    if (value === 'all') {
      setSelectedItems(features);
    } else if (value === 'none') {
      setSelectedItems([]);
    } else if (value === 'some') {
      // focus on the multi select input and open the popover
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Trigger click to open popover if it's not already open
          inputRef.current.click();
        }
      }, 0);
    }
  };

  return (
    <>
      <div
        className={cs.segmentedControlContainer}
        style={{
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'default',
        }}
      >
        <B.SegmentedControl
          options={[
            {label: allText, value: 'all'},
            {label: noneText, value: 'none'},
            {label: someText, value: 'some'},
          ]}
          value={currentSegment}
          onValueChange={disabled ? undefined : handleSegmentChange}
          fill={true}
        />
      </div>
      <div className={cs.featureSelectInputContainer}>
        <MultiSelect
          disabled={disabled}
          className={className}
          popoverProps={popoverProps}
          fill={true}
          items={sortedFeatures}
          itemRenderer={(item, {handleClick}) => (
            <B.MenuItem
              key={item.id}
              text={composeName(item)}
              onClick={handleClick}
              active={selectedItems.includes(item)}
            />
          )}
          itemPredicate={(query, item) => {
            const isSelected = selectedItems.some((f) => f.properties.id === item.properties.id);
            return !isSelected && composeName(item).toLowerCase().includes(query.toLowerCase());
          }}
          itemsEqual={(a, b) => a.id === b.id}
          onItemSelect={(item) => {
            // if feature is already selected, remove it
            if (selectedItems.includes(item)) {
              setSelectedItems(selectedItems.filter((i) => i !== item));
            } else {
              setSelectedItems([...selectedItems, item]);
            }
          }}
          noResults={<B.MenuItem disabled={true} text="No results." />}
          tagRenderer={(item) => <span className={cs.featureTag}>{composeName(item)}</span>}
          tagInputProps={{
            className: cs.tagInput,
            inputRef: inputRef,
            onRemove: (_, index) => setSelectedItems(selectedItems.filter((_, i) => i !== index)),
          }}
          selectedItems={selectedItems}
          placeholder={placeholder || 'Select or type to search...'}
        />
        {!resetButtonHidden && (
          <B.Tooltip position={B.PopoverPosition.TOP} className={cs.resetButton}>
            <B.Button
              text="Reset"
              icon={'reset'}
              minimal
              onClick={() => setSelectedItems(initialSelection)}
            />
            <span>{resetButtonTooltip}</span>
          </B.Tooltip>
        )}
      </div>
    </>
  );
};
